import { useCallback, useState, useEffect } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { useNavigate } from 'react-router';
import { theme, Text, SocialButton, FormInput, Button, CalloutBox, Logo } from '@socrait/react';
import { CredentialResponse, GoogleLogin, useGoogleOAuth } from '@react-oauth/google';

import { HyperLink } from '~/components/HyperLink';
import { Loading } from '~/components/Loading';
import { useLogInMutation } from '~/services/useLoginMutation';
import { useEmailPasswordLoginMutation } from '~/services/useEmailPasswordLoginMutation';
import { showError } from '~/utils/showError';
import { useTestAccountLoginFeature } from '~/featureFlags';
import { useEnvironmentVariables } from '~/hooks/useEnvironmentVariables';

import { handlePrivacyPolicyPress, handleTermsOfServicePress } from '~/constants';
import SparklesUrl from '~/assets/images/sparkles.svg';

export function OldLoginPage() {
  const [scriptLoading, setScriptLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [isEmailPasswordLoginPending, setIsEmailPasswordLoginPending] = useState(false);
  const [showEmailPasswordForm, setShowEmailPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [passwordBlurred, setPasswordBlurred] = useState(false);

  const { mutate } = useLogInMutation();
  const { mutate: emailPasswordMutate } = useEmailPasswordLoginMutation();
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const navigate = useNavigate();
  const isTestAccountLoginEnabled = useTestAccountLoginFeature();
  const { isMockMode } = useEnvironmentVariables();

  // Form validation (only show errors after blur)
  const emailValid = email.trim().length > 0 && email.includes('@');
  const passwordValid = password.trim().length > 0;
  const isFormValid = emailValid && passwordValid;
  const showEmailError = emailBlurred && email.trim().length > 0 && !email.includes('@');
  const showPasswordError = passwordBlurred && password.trim().length === 0;

  // Monitor script loading status
  useEffect(() => {
    setScriptLoading(true);

    // Set a timeout to detect if script loading takes too long
    const timeoutId = setTimeout(() => {
      if (!scriptLoadedSuccessfully) {
        setScriptError(true);
        setScriptLoading(false);
      }
    }, 10000); // 10 seconds timeout

    // When script loads successfully
    if (scriptLoadedSuccessfully) {
      setScriptLoading(false);
      setScriptError(false);
      clearTimeout(timeoutId);
    }

    return () => clearTimeout(timeoutId);
  }, [scriptLoadedSuccessfully]);

  const handleGoogleSso = useCallback(
    (credentialResponse: CredentialResponse) => {
      // Start the spinner.
      setIsLoginPending(true);

      mutate(
        { idToken: credentialResponse.credential! },
        {
          onSuccess: () => {
            // Navigate to the root so that the app router can determine the correct authorized route.
            navigate('/', { replace: true });
          },
          onError: (error) => {
            // Stop the spinner.
            setIsLoginPending(false);

            showError(error, 'Login failed. Please try again, or contact support if the issue persists.');
          },
        },
      );
    },
    [mutate, navigate],
  );

  const handleGoogleSsoError = useCallback(() => {
    setIsLoginPending(false);
    showError(new Error(), 'Login failed. Please try again, or contact support if the issue persists.');
  }, []);

  const retryScriptLoading = useCallback(() => {
    setScriptError(false);
    setScriptLoading(true);
    // Force reload the Google OAuth script
    // This implementation might vary based on how useGoogleOAuth is implemented
    window.location.reload();
  }, []);

  const handleEmailPasswordLogin = useCallback(() => {
    setIsEmailPasswordLoginPending(true);
    emailPasswordMutate(
      { email: email.trim(), password },
      {
        onSuccess: () => {
          // Navigate to the root so that the app router can determine the correct authorized route.
          navigate('/', { replace: true });
        },
        onError: (error) => {
          setIsEmailPasswordLoginPending(false);
          showError(error, 'Login failed. Please try again, or contact support if the issue persists.');
        },
      },
    );
  }, [email, password, emailPasswordMutate, navigate]);

  const handleBackToGoogle = useCallback(() => {
    setShowEmailPasswordForm(false);
    setEmail('');
    setPassword('');
    setEmailBlurred(false);
    setPasswordBlurred(false);
  }, []);

  const handleEmailBlur = useCallback(() => {
    setEmailBlurred(true);
  }, []);

  const handlePasswordBlur = useCallback(() => {
    setPasswordBlurred(true);
  }, []);

  const handleShowEmailPasswordForm = useCallback(() => {
    setShowEmailPasswordForm(true);
  }, []);

  // Render appropriate UI based on script loading state
  const renderContent = () => {
    // Only show full-page loading for Google login, not email/password login, which has its own loading state.
    // In mock mode, skip the Google script loading check — we render a mock button instead.
    if ((!isMockMode && scriptLoading) || isLoginPending) {
      return <Loading />;
    }

    if (scriptError) {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Failed to load Google authentication services.</Text>
          <SocialButton onPress={retryScriptLoading} icon="google" name="Retry" containerStyle={styles.retryButton} />
        </View>
      );
    }

    const canSubmitEmailPasswordForm = isFormValid && !isEmailPasswordLoginPending;

    return (
      <View style={styles.outerContainer}>
        <Image source={{ uri: SparklesUrl }} style={styles.background} resizeMode="contain" />
        <View style={styles.innerContainer}>
          <View style={styles.logoContainer}>
            <Logo />
            <Text style={styles.logoText}>
              socr<Text style={[styles.logoText, { color: theme.colors.primaryPurple }]}>ai</Text>t
            </Text>
          </View>
          {showEmailPasswordForm ? (
            <View style={styles.formContainer}>
              <CalloutBox
                variant="caution"
                message="Unless indicated by support, please use the Google button to log in or sign up."
              />
              <View style={styles.form}>
                <FormInput
                  label="Email"
                  placeholderText="Enter your email"
                  value={email}
                  onChangeText={setEmail}
                  onBlur={handleEmailBlur}
                  onSubmit={() => canSubmitEmailPasswordForm && handleEmailPasswordLogin()}
                  variant="mobile"
                  required
                  hasError={showEmailError}
                  errorMessage={showEmailError ? 'Please enter a valid email address' : undefined}
                  containerStyle={styles.inputContainer}
                />
                <FormInput
                  label="Password"
                  placeholderText="Enter your password"
                  value={password}
                  onChangeText={setPassword}
                  onBlur={handlePasswordBlur}
                  onSubmit={() => canSubmitEmailPasswordForm && handleEmailPasswordLogin()}
                  variant="mobile"
                  required
                  shadow
                  hasError={showPasswordError}
                  errorMessage={showPasswordError ? 'Password is required' : undefined}
                  containerStyle={styles.inputContainer}
                />
                <Button
                  name="Login"
                  variant="primary"
                  disabled={!canSubmitEmailPasswordForm}
                  loading={isEmailPasswordLoginPending}
                  onPress={handleEmailPasswordLogin}
                  containerStyle={styles.submitButton}
                />
              </View>
              <HyperLink onPress={handleBackToGoogle} style={styles.subtleLink}>
                Back to Google login
              </HyperLink>
            </View>
          ) : (
            <View style={styles.formContainer}>
              <View style={styles.form}>
                {isMockMode ? (
                  <View style={styles.mockButtons}>
                    <Button
                      name="Mock Sign In"
                      variant="primary"
                      onPress={() => handleGoogleSso({ credential: 'mock-id-token' } as CredentialResponse)}
                    />
                    <Button
                      name="Mock Set Up"
                      variant="secondary"
                      onPress={() => handleGoogleSso({ credential: 'mock-setup' } as CredentialResponse)}
                    />
                  </View>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSso}
                    onError={handleGoogleSsoError}
                    text="signin_with"
                    size="large"
                    shape="circle"
                    theme="outline"
                  />
                )}
              </View>
            </View>
          )}
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText} numberOfLines={1}>
            <HyperLink onPress={handleTermsOfServicePress} variant="subdued">
              Terms of Service
            </HyperLink>
            {' | '}
            <HyperLink onPress={handlePrivacyPolicyPress} variant="subdued">
              Privacy Policy
            </HyperLink>
            {isTestAccountLoginEnabled && (
              <>
                {' | '}
                <HyperLink onPress={handleShowEmailPasswordForm} variant="subdued">
                  Partner Login
                </HyperLink>
              </>
            )}
          </Text>
          <Text style={styles.footerText} numberOfLines={1}>
            (c) 2025 All Rights Reserved
          </Text>
        </View>
      </View>
    );
  };

  return renderContent();
}

const styles = StyleSheet.create({
  outerContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    position: 'relative',
  },
  background: {
    position: 'absolute',
    zIndex: 0,
    top: '5%',
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '90%',
  },
  innerContainer: {
    display: 'flex',
    width: 304,
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.xxlarge * 2,
    zIndex: 1,
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoText: {
    fontWeight: theme.fontWeights.bold,
    fontSize: 50,
    marginTop: theme.spacing.medium,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 30,
    alignSelf: 'stretch',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    alignSelf: 'stretch',
  },
  mockButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
  },
  footer: {
    maxWidth: 500,
    position: 'absolute',
    bottom: 34,
    zIndex: 1,
  },
  errorText: {
    color: theme.colors.red,
    fontFamily: theme.fonts.regular,
    fontSize: theme.fontSizes.small,
    textAlign: 'center',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    height: '100%',
    padding: 20,
  },
  retryButton: {
    alignSelf: 'auto',
    marginTop: 16,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: theme.colors.primaryPurple,
  },
  footerText: {
    textAlign: 'center',
    fontSize: theme.fontSizes.xsmall,
  },
  backButton: {
    alignSelf: 'flex-start',
    marginBottom: theme.spacing.small,
  },
  inputContainer: {
    alignSelf: 'stretch',
  },
  submitButton: {
    alignSelf: 'stretch',
    marginTop: theme.spacing.medium,
  },
  subtleLink: {
    fontSize: theme.fontSizes.xsmall,
  },
});
